import { FETCH_AUDIT_SUCCESS } from 'modules/audits/actionTypes';
import { AuditResponseTypeEnum } from 'modules/audits/types/resources/audits.enum';
import type { StoredAudit } from 'modules/audits/types/stored-audit';
import type { Action } from 'modules/global/generic.types';
import { FETCH_INTERVENTION_SUCCESS } from 'modules/interventions/actionTypes';
import type { DetailedIntervention, DetailedTire } from 'modules/interventions/types/projections/detailed-intervention';
import { FETCH_TIRE_BRAND_SUCCESS } from 'modules/tireBrands/actionTypes';
import type { TireBrand } from 'modules/tireBrands/types';
import type { EntitySlice } from 'utils/ReduxUtils';
import { createEntityAdapter, createReducer } from 'utils/ReduxUtils';

type TireBrandState = EntitySlice<TireBrand>;

const tireBrandAdapter = createEntityAdapter<TireBrand>({ selectId: ({ id }) => id as string });

const extractTireBrands = (tires: DetailedTire[] = []): TireBrand[] =>
  tires.reduce<TireBrand[]>((acc, tire) => [...acc, ...(tire.tireBrand ? [tire.tireBrand] : [])], []);

const fetchIntervention = (state: TireBrandState, { payload: intervention }: Action<DetailedIntervention>) => {
  if (intervention!.tireReport?.tires) {
    return tireBrandAdapter.addMany(state, extractTireBrands(intervention!.tireReport.tires));
  }
  return state;
};

const fetchAudit = (state: TireBrandState, { payload: audit }: Action<StoredAudit>) => {
  const tireBrands = audit!.sections
    .flatMap(section => section.questions)
    .reduce<TireBrand[]>((acc, response) => {
      if (response.type !== AuditResponseTypeEnum.TIRE_REPORT) {
        return acc;
      }
      return [...acc, ...extractTireBrands(response.tires)];
    }, []);

  return tireBrands.length ? tireBrandAdapter.addMany(state, tireBrands) : state;
};

export default createReducer<TireBrandState>(tireBrandAdapter.getInitialState(), {
  [FETCH_TIRE_BRAND_SUCCESS]: (state, { payload }: Action<{ content: TireBrand[] }>) => tireBrandAdapter.addMany(state, payload!.content),
  [FETCH_INTERVENTION_SUCCESS]: fetchIntervention,
  [FETCH_AUDIT_SUCCESS]: fetchAudit,
});
